/**
 * RichTextEditor Component
 * A WYSIWYG editor using Tiptap for React 19 compatibility.
 * Supports: bold, italic, underline, lists, headings, paragraphs.
 * Preserves formatting when pasting from Word, Google Docs, etc.
 */
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { 
  Bold, 
  Italic, 
  List, 
  ListOrdered, 
  Heading1, 
  Heading2, 
  Quote,
  Undo,
  Redo,
  RemoveFormatting
} from 'lucide-react';

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const buttonClass = (isActive) => 
    `p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors ${
      isActive ? 'bg-slate-200 dark:bg-slate-700 text-[#0047AB]' : 'text-slate-600 dark:text-slate-400'
    }`;

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 rounded-t-lg">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={buttonClass(editor.isActive('bold'))}
        title="Bold (Ctrl+B)"
      >
        <Bold className="w-4 h-4" />
      </button>
      
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={buttonClass(editor.isActive('italic'))}
        title="Italic (Ctrl+I)"
      >
        <Italic className="w-4 h-4" />
      </button>

      <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />

      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={buttonClass(editor.isActive('heading', { level: 1 }))}
        title="Heading 1"
      >
        <Heading1 className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={buttonClass(editor.isActive('heading', { level: 2 }))}
        title="Heading 2"
      >
        <Heading2 className="w-4 h-4" />
      </button>

      <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />

      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={buttonClass(editor.isActive('bulletList'))}
        title="Bullet List"
      >
        <List className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={buttonClass(editor.isActive('orderedList'))}
        title="Numbered List"
      >
        <ListOrdered className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={buttonClass(editor.isActive('blockquote'))}
        title="Quote"
      >
        <Quote className="w-4 h-4" />
      </button>

      <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />

      <button
        type="button"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className={`${buttonClass(false)} disabled:opacity-30`}
        title="Undo (Ctrl+Z)"
      >
        <Undo className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className={`${buttonClass(false)} disabled:opacity-30`}
        title="Redo (Ctrl+Y)"
      >
        <Redo className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        className={buttonClass(false)}
        title="Clear Formatting"
      >
        <RemoveFormatting className="w-4 h-4" />
      </button>
    </div>
  );
};

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = 'Enter text here...', 
  minHeight = '150px',
  className = ''
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Enable all default formatting options
        heading: {
          levels: [1, 2, 3]
        }
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty'
      })
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Only call onChange if content actually changed
      if (onChange) {
        // Return empty string if only contains empty paragraph
        const isEmpty = html === '<p></p>' || html === '';
        onChange(isEmpty ? '' : html);
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm dark:prose-invert max-w-none focus:outline-none'
      }
    }
  });

  // Sync external value changes to editor
  // Only update if the value is different from current editor content
  if (editor && value !== undefined) {
    const currentContent = editor.getHTML();
    const isEmpty = currentContent === '<p></p>' || currentContent === '';
    const valueIsEmpty = !value || value === '<p></p>';
    
    if (valueIsEmpty && isEmpty) {
      // Both empty, no update needed
    } else if (value !== currentContent) {
      // Value changed externally, update editor
      // Use setTimeout to avoid React rendering conflicts
      setTimeout(() => {
        editor.commands.setContent(value || '', false);
      }, 0);
    }
  }

  return (
    <div className={`rich-text-editor border rounded-lg overflow-hidden ${className}`}>
      <MenuBar editor={editor} />
      <div 
        className="bg-white dark:bg-slate-900"
        style={{ minHeight }}
      >
        <EditorContent 
          editor={editor} 
          className="p-3"
          style={{ minHeight }}
        />
      </div>
      <style>{`
        .rich-text-editor .ProseMirror {
          min-height: ${minHeight};
          padding: 12px;
          outline: none;
        }
        .rich-text-editor .ProseMirror p {
          margin-bottom: 0.5em;
        }
        .rich-text-editor .ProseMirror p:last-child {
          margin-bottom: 0;
        }
        .rich-text-editor .ProseMirror h1 {
          font-size: 1.5em;
          font-weight: 700;
          margin-bottom: 0.5em;
        }
        .rich-text-editor .ProseMirror h2 {
          font-size: 1.25em;
          font-weight: 600;
          margin-bottom: 0.5em;
        }
        .rich-text-editor .ProseMirror h3 {
          font-size: 1.1em;
          font-weight: 600;
          margin-bottom: 0.5em;
        }
        .rich-text-editor .ProseMirror ul,
        .rich-text-editor .ProseMirror ol {
          margin-left: 1.5em;
          margin-bottom: 0.5em;
        }
        .rich-text-editor .ProseMirror ul {
          list-style-type: disc;
        }
        .rich-text-editor .ProseMirror ol {
          list-style-type: decimal;
        }
        .rich-text-editor .ProseMirror blockquote {
          border-left: 3px solid #0047AB;
          padding-left: 1em;
          margin-left: 0;
          margin-bottom: 0.5em;
          color: #64748b;
        }
        .rich-text-editor .ProseMirror strong {
          font-weight: 600;
        }
        .rich-text-editor .is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #94a3b8;
          pointer-events: none;
          height: 0;
        }
        .dark .rich-text-editor .is-editor-empty:first-child::before {
          color: #64748b;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
